import React, { useState, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Platform, Alert, Pressable, Linking } from 'react-native';
import { YStack, XStack, Text, Button, Input, Card, H4, Paragraph } from 'tamagui';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useDispatch } from 'react-redux';
import { AntDesign, Entypo, MaterialCommunityIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import apis from '@api/api';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import { WebView } from 'react-native-webview';
import { useQuery } from '@tanstack/react-query';

interface ResumeData {
  personalInformation?: any;
  professionalSummary?: string;
  workExperience?: any[];
  education?: any[];
  skills?: any;
  projects?: any[];
  certifications?: any[];
  languages?: any[];
  additionalSections?: any;
  metadata?: any;
}

const Resumes = () => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [extractionInProgress, setExtractionInProgress] = useState(false);

  // Fetch available templates
  const { data: templates, isLoading: templatesLoading } = useQuery({
    queryKey: ['resumeTemplates'],
    queryFn: async () => {
      const response = await apis.getResumeTemplates();
      // Backend returns { success: true, data: templates }
      const responseData = response.data || response;
      return responseData.success && responseData.data ? responseData.data : [];
    }
  });

  const handleFileUpload = useCallback(async () => {
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Selecting resume file...' } });

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/msword', // .doc
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        ],
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        dispatch({ type: HIDE_LOADING_MODAL });
        return;
      }

      const file = result.assets[0];
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Reading file...' } });

      // Read file as base64
      let base64Data: string;
      try {
        if (file.uri.startsWith('data:')) {
          base64Data = file.uri;
        } else if (file.uri.includes('base64,')) {
          base64Data = file.uri;
        } else if (Platform.OS === 'web') {
          // Web platform: fetch blob and convert to base64
          const response = await fetch(file.uri);
          const blob = await response.blob();
          base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } else if (file.uri.startsWith('file://') || file.uri.startsWith('content://')) {
          // Native platform: use FileSystem
          try {
            const fileInfo = await FileSystem.getInfoAsync(file.uri);
            if (!fileInfo.exists) {
              throw new Error('File does not exist at URI');
            }

            const fileData = await FileSystem.readAsStringAsync(file.uri, {
              encoding: 'base64' as any,
            });
            base64Data = `data:${file.mimeType || 'application/pdf'};base64,${fileData}`;
          } catch (fileSystemError) {
            console.log('FileSystem read failed, trying fetch:', fileSystemError);
            // Fallback to fetch for content:// URIs or other cases
            const response = await fetch(file.uri);
            const blob = await response.blob();
            base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const result = reader.result as string;
                resolve(result);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          }
        } else {
          // Unknown URI format - try fetch as fallback
          const response = await fetch(file.uri);
          const blob = await response.blob();
          base64Data = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        }

        // Validate we got actual base64 data
        if (!base64Data || base64Data.length < 100) {
          throw new Error('Failed to read file data - data too short or empty');
        }

        // Ensure it's a proper data URL
        if (!base64Data.startsWith('data:')) {
          throw new Error('Invalid file data format');
        }
      } catch (readError) {
        console.error('Error reading file:', readError);
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: `Failed to read file: ${readError.message || 'Please try again.'}`, type: 'error' }
        });
        return;
      }

      await extractResumeFromFile(base64Data, file.mimeType || 'application/pdf');

    } catch (error: any) {
      console.error('File upload error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: error.message || 'Failed to upload file', type: 'error' }
      });
    }
  }, [dispatch]);

  const extractResumeFromFile = async (fileData: string, fileType: string) => {
    setExtractionInProgress(true);
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Extracting resume data...' } });

    try {
      const response = await apis.extractResume(fileData, fileType);
      const responseData = response.data || response;

      if (responseData.success && responseData.data) {
        setResumeData(responseData.data);
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Resume extracted successfully!', type: 'success' }
        });
      } else {
        throw new Error(responseData.error || 'Failed to extract resume');
      }
    } catch (error: any) {
      console.error('Resume extraction error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: error.message || 'Failed to extract resume data', type: 'error' }
      });
    } finally {
      setExtractionInProgress(false);
    }
  };

  const extractResumeFromLinkedIn = async () => {
    if (!linkedinUrl.trim()) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please enter a LinkedIn profile URL', type: 'error' }
      });
      return;
    }

    setExtractionInProgress(true);
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Extracting resume from LinkedIn...' } });

    try {
      const response = await apis.extractResume(undefined, undefined, linkedinUrl.trim());
      const responseData = response.data || response;

      if (responseData.success && responseData.data) {
        setResumeData(responseData.data);
        setLinkedinUrl('');
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: { content: 'Resume extracted successfully!', type: 'success' }
        });
      } else {
        throw new Error(responseData.error || 'Failed to extract resume');
      }
    } catch (error: any) {
      console.error('LinkedIn extraction error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: error.message || 'Failed to extract resume from LinkedIn', type: 'error' }
      });
    } finally {
      setExtractionInProgress(false);
    }
  };

  const applyTemplate = async (templateId: string) => {
    if (!resumeData) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please extract resume data first', type: 'error' }
      });
      return;
    }

    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Applying template...' } });

    try {
      const response = await apis.applyResumeTemplate(templateId, resumeData);
      const responseData = response.data || response;

      if (responseData.success && responseData.data?.htmlContent) {
        setSelectedTemplate(templateId);
        setPreviewHtml(responseData.data.htmlContent);
        dispatch({ type: HIDE_LOADING_MODAL });
      } else {
        throw new Error(responseData.error || 'Failed to apply template');
      }
    } catch (error: any) {
      console.error('Template application error:', error);
      dispatch({ type: HIDE_LOADING_MODAL });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: error.message || 'Failed to apply template', type: 'error' }
      });
    }
  };

  const downloadResume = async (format: 'html' | 'pdf') => {
    if (!previewHtml) {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Please apply a template first', type: 'error' }
      });
      return;
    }

    if (format === 'html') {
      // For HTML, create a blob and download
      if (Platform.OS === 'web') {
        const blob = new Blob([previewHtml], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `resume-${Date.now()}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // For native, open in browser or save to file system
        Alert.alert('Download', 'HTML download is available on web platform');
      }
    } else if (format === 'pdf') {
      // PDF generation would use html2pdf.js on web
      if (Platform.OS === 'web') {
        try {
          const html2pdf = (await import('html2pdf.js')).default;
          const element = document.createElement('div');
          element.innerHTML = previewHtml;
          document.body.appendChild(element);

          const opt = {
            margin: 0.5,
            filename: `resume-${Date.now()}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
          };

          await html2pdf().set(opt).from(element).save();
          document.body.removeChild(element);
        } catch (error) {
          console.error('PDF generation error:', error);
          dispatch({
            type: SHOW_AUTH_MODAL,
            data: { content: 'Failed to generate PDF', type: 'error' }
          });
        }
      } else {
        Alert.alert('Download', 'PDF download is available on web platform');
      }
    }
  };

  return (
    <DefaultScreen>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20 }}>
        <YStack flex={1} gap={20}>
          <YStack justifyContent="space-between" alignItems="center">
            <YStack>
              <H4 color={theme ? '#1e293b' : '#e2e8f0'}>Resume Extraction & Templates</H4>

              {/* Upload Section */}
              <Card padding={20} backgroundColor={theme ? '#ffffff' : '#1e293b'} borderRadius={12}>
                <YStack gap={15}>
                  <Text fontSize={16} fontWeight="600" color={theme ? '#1e293b' : '#e2e8f0'}>
                    Upload Resume
                  </Text>

                  <Button
                    onPress={handleFileUpload}
                    disabled={extractionInProgress}
                    backgroundColor="#3b82f6"
                    color="white"
                    minHeight={120}
                    borderRadius={8}
                    paddingVertical={12}
                  >
                    <YStack alignItems="center" gap={8}>
                      <MaterialCommunityIcons name="file-document" size={20} color="white" />
                      <Text color="white" fontWeight="600">
                        {extractionInProgress ? 'Processing...' : 'Upload PDF/DOCX'}
                      </Text>
                    </YStack>
                  </Button>

                  <Text fontSize={12} color={theme ? '#64748b' : '#94a3b8'} textAlign="center">
                    OR
                  </Text>

                  <YStack gap={10}>
                    <Input
                      placeholder="LinkedIn Profile URL"
                      value={linkedinUrl}
                      onChangeText={setLinkedinUrl}
                      backgroundColor={theme ? '#f8fafc' : '#334155'}
                      borderColor={theme ? '#e2e8f0' : '#475569'}
                      color={theme ? '#1e293b' : '#e2e8f0'}
                      placeholderTextColor={theme ? '#94a3b8' : '#64748b'}
                    />
                    <Button
                      onPress={extractResumeFromLinkedIn}
                      disabled={extractionInProgress || !linkedinUrl.trim()}
                      backgroundColor="#0A66C2"
                      color="white"
                      borderRadius={8}
                      paddingVertical={12}
                    >
                      <XStack alignItems="center" gap={8}>
                        <Entypo name="linkedin" size={20} color="white" />
                        <Text color="white" fontWeight="600">
                          Extract from LinkedIn
                        </Text>
                      </XStack>
                    </Button>
                  </YStack>
                </YStack>
              </Card>

              {/* Template Selection */}
              {resumeData && (
                <Card padding={20} backgroundColor={theme ? '#ffffff' : '#1e293b'} borderRadius={12}>
                  <YStack gap={15}>
                    <Text fontSize={16} fontWeight="600" color={theme ? '#1e293b' : '#e2e8f0'}>
                      Select Template
                    </Text>

                    {templatesLoading ? (
                      <Text color={theme ? '#64748b' : '#94a3b8'}>Loading templates...</Text>
                    ) : templates && templates.length > 0 ? (
                      <XStack gap={10}>
                        {templates.map((template: any) => (
                          <Pressable
                            key={template.id}
                            onPress={() => applyTemplate(template.id)}
                            style={{
                              padding: 15,
                              borderRadius: 8,
                              backgroundColor: selectedTemplate === template.id
                                ? (theme ? '#eff6ff' : '#1e3a5f')
                                : (theme ? '#f8fafc' : '#334155'),
                              borderWidth: selectedTemplate === template.id ? 2 : 1,
                              borderColor: selectedTemplate === template.id ? '#3b82f6' : (theme ? '#e2e8f0' : '#475569')
                            }}
                          >
                            <Text fontSize={14} fontWeight="600" color={theme ? '#1e293b' : '#e2e8f0'}>
                              {template.name}
                            </Text>
                            <Text fontSize={12} color={theme ? '#64748b' : '#94a3b8'} marginTop={4}>
                              {template.description}
                            </Text>
                          </Pressable>
                        ))}
                      </XStack>
                    ) : (
                      <Text color={theme ? '#64748b' : '#94a3b8'}>No templates available</Text>
                    )}
                  </YStack>
                </Card>
              )}
            </YStack>
            <YStack gap={20}>

              {/* Preview Section */}
              {previewHtml && (
                <Card padding={20} backgroundColor={theme ? '#ffffff' : '#1e293b'} borderRadius={12}>
                  <YStack gap={15}>
                    <XStack justifyContent="space-between" alignItems="center">
                      <Text fontSize={16} fontWeight="600" color={theme ? '#1e293b' : '#e2e8f0'}>
                        Preview
                      </Text>
                      <XStack gap={10}>
                        {Platform.OS === 'web' && (
                          <>
                            <Button
                              onPress={() => downloadResume('html')}
                              size="$3"
                              backgroundColor="#10b981"
                              color="white"
                            >
                              <Text color="white" fontSize={12}>HTML</Text>
                            </Button>
                            <Button
                              onPress={() => downloadResume('pdf')}
                              size="$3"
                              backgroundColor="#ef4444"
                              color="white"
                            >
                              <Text color="white" fontSize={12}>PDF</Text>
                            </Button>
                          </>
                        )}
                      </XStack>
                    </XStack>

                    {Platform.OS === 'web' ? (
                      <View style={{ height: 800, borderWidth: 1, borderColor: theme ? '#e2e8f0' : '#475569', borderRadius: 8, overflow: 'hidden' }}>
                        <iframe
                          srcDoc={previewHtml}
                          style={{ width: '100%', height: '100%', border: 'none' }}
                          title="Resume Preview"
                        />
                      </View>
                    ) : (
                      <View style={{ height: 600, borderRadius: 8, overflow: 'hidden' }}>
                        <WebView
                          source={{ html: previewHtml }}
                          style={{ backgroundColor: 'transparent' }}
                          scalesPageToFit={true}
                        />
                      </View>
                    )}
                  </YStack>
                </Card>
              )}

              {/* Extracted Data Summary */}
              {resumeData && !previewHtml && (
                <Card padding={20} backgroundColor={theme ? '#ffffff' : '#1e293b'} borderRadius={12}>
                  <YStack gap={15}>
                    <Text fontSize={16} fontWeight="600" color={theme ? '#1e293b' : '#e2e8f0'}>
                      Extracted Information
                    </Text>

                    {resumeData.personalInformation && (
                      <YStack gap={5}>
                        <Text fontSize={14} fontWeight="600" color={theme ? '#475569' : '#cbd5e1'}>
                          Personal Information
                        </Text>
                        <Text fontSize={12} color={theme ? '#64748b' : '#94a3b8'}>
                          Name: {resumeData.personalInformation.fullName || 'N/A'}
                        </Text>
                        <Text fontSize={12} color={theme ? '#64748b' : '#94a3b8'}>
                          Email: {resumeData.personalInformation.email || 'N/A'}
                        </Text>
                      </YStack>
                    )}

                    {resumeData.workExperience && resumeData.workExperience.length > 0 && (
                      <YStack gap={5}>
                        <Text fontSize={14} fontWeight="600" color={theme ? '#475569' : '#cbd5e1'}>
                          Work Experience: {resumeData.workExperience.length} position(s)
                        </Text>
                      </YStack>
                    )}

                    {resumeData.education && resumeData.education.length > 0 && (
                      <YStack gap={5}>
                        <Text fontSize={14} fontWeight="600" color={theme ? '#475569' : '#cbd5e1'}>
                          Education: {resumeData.education.length} degree(s)
                        </Text>
                      </YStack>
                    )}

                    <Text fontSize={12} color={theme ? '#64748b' : '#94a3b8'} fontStyle="italic">
                      Select a template above to preview the formatted resume
                    </Text>
                  </YStack>
                </Card>
              )}
            </YStack>
          </YStack>
        </YStack>
      </ScrollView>
    </DefaultScreen>
  );
};

export default Resumes;
